import {
  useCallback,
  useEffect,
  useState } from 'react';
import {
  Container,
  Typography,
  Box,
  MenuItem,
  Paper,
  List,
  ListItem,
  ListItemText,
  Button
} from '@mui/material';
import Select from '../components/SearchableSelect';

import { useTranslation } from 'react-i18next';
import SidebarLayout from '../components/sidebarLayout';
import { getAllProdotti, ordinaProdotti } from '../APIs/ProductsAPI';
import { getAllCategorie } from '../APIs/CategoriesAPI';
import { getTenantTranslation } from '../utils/getTenantTranslation';

import {
  DndContext, useSensor,
  useSensors, PointerSensor,
  closestCenter
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SetupWSEventListener } from '../utils/setupWSEventListener';
import { useSnackbar } from '../contexts/SnackBarContext';
import { mergeReorderedProducts } from '../utils/menuOrdering';

const productHasCategory = (product, categoryId) => {
  const normalizedCategoryId = Number(categoryId);
  if (!Number.isInteger(normalizedCategoryId)) return false;

  if (Array.isArray(product?.categories) && product.categories.length > 0) {
    return product.categories.some((entry) =>
      Number(entry?.categoryId ?? entry?.category?.id) === normalizedCategoryId
    );
  }

  return Number(product?.categoryId) === normalizedCategoryId;
};

// Componente drag
const SpostaProdotto = ({ product, id }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  return (
    <ListItem
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        cursor: 'grab',
      }}
      {...attributes}
      {...listeners}
      divider
    >
      <ListItemText primary={product.name} secondary={`€ ${product.price}`} />
    </ListItem>
  );
};

const ConfigurazioneMenu = () => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const tenantType = localStorage.getItem('tenantType');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [originalOrder, setOriginalOrder] = useState([]);
  const [isOrderChanged, setIsOrderChanged] = useState(false);
  const [dndPayload, setDndPayload] = useState([]);
  const sensors = useSensors(useSensor(PointerSensor));

  const token = localStorage.getItem('accessToken');
  const tenant = localStorage.getItem('tenantId');
  const fetchData = useCallback(async () => {
    try {
      getAllProdotti(token, tenant)
        .then((data) => {
          const ordinati = data.sort((a, b) => a.order - b.order);
          setProducts(ordinati);
        })
        .catch((error) => console.error("Errore nel caricamento prodotti:", error.message));
      getAllCategorie(token, tenant)
        .then((data) => setCategories(data))
        .catch((error) => console.error("Errore nel caricamento prodotti:", error.message));
    } catch (error) {
      console.error("Errore nel caricamento categorie:", error.message);
    }
  }, [tenant, token]);

  useEffect(() => {
    if (products.length > 0 && originalOrder.length === 0) {
      setOriginalOrder(products.map(p => p.id));
    }
  }, [products, originalOrder.length]);

  useEffect(() => {
    fetchData();

    const cleanup = SetupWSEventListener(fetchData);
    return cleanup;
  }, [fetchData]);


  const handleSelectCategory = (id) => {
    const found = categories.find(c => Number(c.id) === Number(id)) || null;
    const categoryProducts = (found
      ? products.filter((product) => productHasCategory(product, found.id))
      : products
    ).filter((product) => isVisibleNow(product.visibility));

    setSelectedCategory(found);
    setOriginalOrder(categoryProducts.map((product) => product.id));
    setDndPayload([]);
    setIsOrderChanged(false);
  };

  const isVisibleNow = (visibilities) => {
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    return visibilities.some(({ startTime, endTime }) => {
      const [startH, startM] = startTime.split(':').map(Number);
      const [endH, endM] = endTime.split(':').map(Number);
      const start = startH * 60 + startM;
      const end = endH * 60 + endM;

      return currentMinutes >= start && currentMinutes <= end;
    });
  };

  const prodottiFiltrati = (selectedCategory
    ? products.filter(p => productHasCategory(p, selectedCategory.id))
    : products
  ).filter(p => isVisibleNow(p.visibility));

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = prodottiFiltrati.findIndex(p => p.id === active.id);
      const newIndex = prodottiFiltrati.findIndex(p => p.id === over?.id);

      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(prodottiFiltrati, oldIndex, newIndex);
      setProducts((currentProducts) => mergeReorderedProducts(currentProducts, reordered));

      // Costruisci payload per API: lo stesso array di prima, ma con l'order aggiornato
      const reorderPayload = [];
      for (let i = 0; i < reordered.length; i++) {
        reorderPayload.push({
          productId: reordered[i].id,
          newOrder: i + 1,
        })
      }

      setDndPayload(reorderPayload);

      // confronto con ordine originale
      const isDifferent = reordered.map(p => p.id).some(
        (id, index) => id !== originalOrder[index]
      );

      setIsOrderChanged(isDifferent);
    }
  };

  const sendPayload = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const tenant = localStorage.getItem('tenantId');
      await ordinaProdotti(dndPayload, token, tenant);
      setOriginalOrder(prodottiFiltrati.map((product) => product.id));
      setDndPayload([]);
      setIsOrderChanged(false);
    } catch (error) {
      handleAlert(t("menu_alert.error_sorting") + error.message, "error");
    }
  }

  function handleAlert(msg, type) {
    showSnackbar(msg, type)
  }

  return (
    <SidebarLayout className="admin-responsive-page">
      <Container sx={{ mt: { xs: 2, md: 4 } }}>
        <Typography variant="h4" gutterBottom>{getTenantTranslation(t, tenantType, 'menu_config.page')}</Typography>

        <Paper sx={{ p: 2, mb: 4 }}>
          <Box sx={{ mt: 2, mb: 2 }}>
            <Select
              fullWidth
              displayEmpty
              value={selectedCategory?.id || ''}
              onChange={(e) => handleSelectCategory(e.target.value)}
            >
              <MenuItem value="">{t('menu_config.select_category')}</MenuItem>
              {categories.map(cat => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
            <Button onClick={sendPayload}
              variant='contained' sx={{ mt: 2 }}
              disabled={dndPayload.length === 0 || !isOrderChanged}>
              {t('menu_config.save_order')}
            </Button>
          </Box>

          {/* <Typography variant="h6">{t('menu_config.product_ordering')}</Typography> */}
          {prodottiFiltrati.length > 0 ?
            (<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={prodottiFiltrati.map(p => p.id)} strategy={verticalListSortingStrategy}>

                <List>
                  {prodottiFiltrati.map(prod => (
                    <SpostaProdotto
                      key={prod.id}
                      id={prod.id}
                      product={prod}
                    />
                  ))}
                </List>
              </SortableContext>
            </DndContext>)
            :
            (
              <Box display={"flex"} justifyContent={"center"}
                sx={{ p: { xs: 3, md: 10 } }}>
                <Typography variant="outlined">
                  {t("menu_config.noProductsMenuFound")}
                </Typography>
              </Box>
            )}
        </Paper>
      </Container>
    </SidebarLayout>
  );
};

export default ConfigurazioneMenu;
